import { useEffect, useState, useCallback } from 'react';
import api from '../services/api';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const urlBase64ToUint8Array = (b64) => {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
  return arr;
};

const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

/**
 * React hook that wraps the entire Web Push lifecycle for Lutrom Portal:
 * - service-worker registration
 * - permission gating (must be inside a real user click on iOS)
 * - subscription creation + POSTing it to the backend
 * - reading the current subscription state on mount
 * - tearing it down ("Disable notifications")
 *
 * Returns a small, intentionally-flat surface so any page can render a
 * Switch + button without needing to know any of the Web Push internals.
 */
export const usePushNotifications = () => {
  const supported = isPushSupported();
  const [permission, setPermission] = useState(() =>
    supported ? Notification.permission : 'unsupported',
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
      setPermission(Notification.permission);
    } catch (e) {
      // Most likely no SW registered yet; let subscribe() handle that branch.
    }
  }, [supported]);

  useEffect(() => {
    if (!supported) return;
    // Register the service worker exactly once on mount. We don't auto-prompt
    // — iOS requires the prompt to come from a real user click.
    navigator.serviceWorker
      .register('/service-worker.js')
      .then(refresh)
      .catch(() => { /* swallowed — surfaced via subscribe() if user clicks */ });
  }, [supported, refresh]);

  const subscribe = useCallback(async () => {
    if (!supported) {
      setError('Push notifications are not supported on this device');
      return false;
    }
    setBusy(true);
    setError('');
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setError('Notifications were blocked — enable them in your device settings to continue');
        return false;
      }
      const reg = await navigator.serviceWorker.register('/service-worker.js');
      // VAPID public key from backend
      const { data: keyRes } = await api.get('/push/vapid-public-key');
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyRes.publicKey),
      });
      const sj = sub.toJSON();
      await api.post('/push/subscribe', {
        endpoint: sj.endpoint,
        keys: sj.keys,
        expirationTime: sj.expirationTime,
        userAgent: navigator.userAgent.slice(0, 240),
      });
      setSubscribed(true);
      return true;
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Could not enable notifications');
      return false;
    } finally {
      setBusy(false);
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    setBusy(true);
    setError('');
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        try { await api.post('/push/unsubscribe', { endpoint: sub.endpoint }); } catch (_e) { /* best-effort */ }
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } catch (e) {
      setError(e?.message || 'Could not disable notifications');
    } finally {
      setBusy(false);
    }
  }, [supported]);

  return {
    supported,
    permission,        // 'default' | 'granted' | 'denied' | 'unsupported'
    subscribed,
    busy,
    error,
    subscribe,
    unsubscribe,
    refresh,
    apiBase: API_BASE,
  };
};

export default usePushNotifications;
